// src/components/FinancialReportScreen.jsx
// Relatório financeiro premium.
// Endpoint: GET /psy/finance/report?id=ID_USUARIO
// Padrões: AppSidebar, token via x-new-token, erro real do backend e UI responsiva isolada em .frep-*.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/financialReport.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const MONTHS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const TRANSACTION_FILTERS = [
  { value: "todas", label: "Todas" },
  { value: "receita", label: "Receitas" },
  { value: "despesa", label: "Despesas" },
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function moneyBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function percentBR(value) {
  const number = safeNumber(value);

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(number);
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getTimeBR(value) {
  const date = parseDate(value);

  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getMonthLabel(mes, fallbackIndex = 0) {
  const value = Number(mes);

  if (Number.isFinite(value)) {
    if (value >= 1 && value <= 12) return MONTHS[value - 1];
    if (value >= 0 && value <= 11) return MONTHS[value];
  }

  return MONTHS[fallbackIndex % 12];
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();
  if (!clean) return "";
  return clean.split(/\s+/)[0];
}

function getBalanceTone(value) {
  const number = safeNumber(value);

  if (number > 0) return "good";
  if (number < 0) return "bad";
  return "neutral";
}

function getFinancialHealth({ receita, despesa, lucro }) {
  const receitaNumber = safeNumber(receita);
  const despesaNumber = safeNumber(despesa);
  const lucroNumber = safeNumber(lucro);
  const margem = receitaNumber > 0 ? (lucroNumber / receitaNumber) * 100 : 0;

  if (receitaNumber <= 0 && despesaNumber <= 0) {
    return {
      label: "Sem movimento",
      tone: "neutral",
      description: "Ainda não há dados suficientes para leitura financeira.",
      margin: margem,
    };
  }

  if (lucroNumber < 0) {
    return {
      label: "Atenção",
      tone: "bad",
      description: "As despesas estão acima das receitas no período.",
      margin: margem,
    };
  }

  if (margem >= 35) {
    return {
      label: "Saudável",
      tone: "good",
      description: "A margem está confortável para o período analisado.",
      margin: margem,
    };
  }

  if (margem >= 10) {
    return {
      label: "Estável",
      tone: "warning",
      description: "Há lucro, mas vale acompanhar custos e recorrências.",
      margin: margem,
    };
  }

  return {
    label: "Margem baixa",
    tone: "warning",
    description: "O saldo é positivo, mas a margem ainda está apertada.",
    margin: margem,
  };
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || json?.error || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function normalizeMonthlyRevenue(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows
    .map((item, index) => {
      const valor = safeNumber(item?.valor ?? item?.receita ?? item?.total ?? 0);
      const mes = item?.mes ?? item?.month ?? index;

      return {
        mes,
        label: getMonthLabel(mes, index),
        valor,
      };
    })
    .filter((item) => Number.isFinite(item.valor));
}

function normalizeTransactions(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows.map((item, index) => {
    const tipo = String(item?.tipo || item?.type || "").toLowerCase();
    const isIncome = tipo === "receita" || tipo === "income" || tipo === "entrada";
    const isExpense = tipo === "despesa" || tipo === "expense" || tipo === "saida";

    return {
      id: item?._id || item?.id || `${index}`,
      tipo: isIncome ? "receita" : isExpense ? "despesa" : tipo || "despesa",
      descricao: item?.descricao || item?.description || item?.titulo || "Transação",
      valor: safeNumber(item?.valor ?? item?.value ?? item?.total ?? 0),
      data: item?.data || item?.createdAt || item?.dataCadastro || null,
      categoria: item?.categoria || item?.servicoId || item?.category || "",
    };
  });
}

function buildTransactionSearchText(transaction) {
  return normalizeText(
    [
      transaction.tipo,
      transaction.descricao,
      transaction.categoria,
      moneyBR(transaction.valor),
      formatDateBR(transaction.data),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function BarChartSvg({ data = [] }) {
  const rows = normalizeMonthlyRevenue(data);

  if (!rows.length) {
    return (
      <div className="frep-chart-empty">
        <span>📊</span>
        <strong>Sem faturamento mensal.</strong>
        <small>Quando houver dados, o gráfico aparecerá aqui.</small>
      </div>
    );
  }

  const visibleRows = rows.slice(0, 12);
  const maxValue = Math.max(...visibleRows.map((item) => item.valor), 1);
  const maxY = maxValue * 1.18;

  const width = 100;
  const height = 46;
  const left = 5;
  const right = 96;
  const top = 7;
  const base = 39;
  const chartWidth = right - left;
  const gap = 1.35;
  const barWidth = Math.max(2, (chartWidth - gap * (visibleRows.length - 1)) / visibleRows.length);

  return (
    <div className="frep-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="frep-svg"
        role="img"
        aria-label="Faturamento mensal"
        preserveAspectRatio="none"
      >
        {[12, 20, 28, 36].map((y) => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} className="frep-grid" />
        ))}

        <path
          d={`M ${left} ${base} L ${right} ${base}`}
          className="frep-axis"
        />

        {visibleRows.map((item, index) => {
          const value = Math.max(0, item.valor);
          const barHeight = (value / maxY) * (base - top);
          const x = left + index * (barWidth + gap);
          const y = base - barHeight;

          return (
            <g key={`${item.label}-${index}`}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx="1.2"
                className="frep-bar"
              >
                <title>
                  {item.label}: {moneyBR(item.valor)}
                </title>
              </rect>
            </g>
          );
        })}
      </svg>

      <div className="frep-xlabels">
        {visibleRows.map((item, index) => (
          <span key={`${item.label}-${index}`}>{item.label}</span>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ tone, icon, label, value, description }) {
  return (
    <article className={`frep-scard frep-scard--${tone}`}>
      <div className="frep-scard__icon" aria-hidden="true">
        {icon}
      </div>

      <div className="frep-scard__content">
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function FinancialReportSkeleton() {
  return (
    <div className="frep-skeleton" aria-hidden="true">
      <div className="frep-skeleton__hero" />
      <div className="frep-skeleton__metrics">
        <span />
        <span />
        <span />
      </div>
      <div className="frep-skeleton__panel" />
      <div className="frep-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({ onRefresh, onBack }) {
  return (
    <section className="frep-state">
      <div className="frep-state__icon">📑</div>
      <strong>Nenhum dado financeiro disponível.</strong>
      <span>
        Ainda não encontramos movimentações suficientes para montar o relatório.
        Cadastre receitas e despesas ou tente atualizar os dados.
      </span>

      <div className="frep-state__actions">
        <button className="frep-btn frep-btn--ghost-dark" type="button" onClick={onBack}>
          Voltar
        </button>

        <button className="frep-btn frep-btn--primary" type="button" onClick={onRefresh}>
          Atualizar
        </button>
      </div>
    </section>
  );
}

export default function FinancialReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [txFilter, setTxFilter] = useState("todas");
  const [txQuery, setTxQuery] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      toast.error("Sessão expirada. Faça login novamente.");
      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate]);

  const fetchReport = useCallback(
    async ({ silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetch(
          `${API_BASE}/psy/finance/report?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          toast.error("Sessão expirada. Faça login novamente.");
          navigate("/login", { replace: true });
          return;
        }

        if (!response.ok) {
          const message = await readErrorMessage(response);
          toast.error(message);
          setReport(null);
          return;
        }

        const data = await response.json();
        setReport(data || null);
        setLastUpdatedAt(new Date());
      } catch (error) {
        console.error("Erro ao carregar relatório financeiro:", error);
        toast.error("Falha de comunicação com a API.");
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, navigate]
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const receita = safeNumber(report?.receita ?? report?.totalReceita ?? 0);
  const despesa = safeNumber(report?.despesa ?? report?.totalDespesa ?? 0);
  const lucro = safeNumber(
    report?.lucro ?? report?.saldo ?? report?.resultado ?? receita - despesa
  );

  const faturamentoMensal = useMemo(
    () => normalizeMonthlyRevenue(report?.faturamentoMensal || report?.monthlyRevenue || []),
    [report]
  );

  const transacoes = useMemo(
    () => normalizeTransactions(report?.ultimasTransacoes || report?.transactions || []),
    [report]
  );

  const financialHealth = useMemo(
    () => getFinancialHealth({ receita, despesa, lucro }),
    [receita, despesa, lucro]
  );

  const filteredTransactions = useMemo(() => {
    const q = normalizeText(txQuery);

    return transacoes.filter((transaction) => {
      const matchesFilter = txFilter === "todas" || transaction.tipo === txFilter;
      const matchesSearch = !q || buildTransactionSearchText(transaction).includes(q);

      return matchesFilter && matchesSearch;
    });
  }, [transacoes, txFilter, txQuery]);

  const transactionTotals = useMemo(() => {
    const entrada = transacoes
      .filter((transaction) => transaction.tipo === "receita")
      .reduce((sum, transaction) => sum + safeNumber(transaction.valor), 0);

    const saida = transacoes
      .filter((transaction) => transaction.tipo === "despesa")
      .reduce((sum, transaction) => sum + safeNumber(transaction.valor), 0);

    return {
      entrada,
      saida,
      total: transacoes.length,
    };
  }, [transacoes]);

  const goBack = () => navigate("/reports");

  const goFinance = () => navigate("/finance");

  const printReport = () => {
    window.print();
  };

  return (
    <div className="frep-page">
      <div className="frep-bg" aria-hidden="true">
        <span className="frep-bg__orb frep-bg__orb--one" />
        <span className="frep-bg__orb frep-bg__orb--two" />
        <span className="frep-bg__orb frep-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="frep-topbar">
        <button
          className="frep-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="frep-brand">
          <img src={Logo} alt="PsyAssist" className="frep-brand__logo" />
          <div className="frep-brand__text">
            <strong>Relatório financeiro</strong>
            <span>Relatórios • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="frep-top-actions">
          <button
            className="frep-btn frep-btn--ghost frep-btn--desktop"
            type="button"
            onClick={goBack}
          >
            Voltar
          </button>

          <button
            className="frep-btn frep-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || !report}
          >
            Imprimir
          </button>

          <button
            className="frep-btn frep-btn--primary"
            type="button"
            onClick={() => fetchReport({ silent: true })}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="frep-main">
        {loading ? (
          <FinancialReportSkeleton />
        ) : !report ? (
          <EmptyState onRefresh={() => fetchReport({ silent: true })} onBack={goBack} />
        ) : (
          <>
            <section className="frep-hero">
              <div>
                <span className="frep-kicker">Visão financeira</span>
                <h1>Financeiro</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe receitas, despesas, lucro e as últimas movimentações
                  do consultório em uma leitura rápida e executiva.
                </p>
              </div>

              <aside className={`frep-hero-card frep-hero-card--${financialHealth.tone}`}>
                <div className="frep-hero-card__icon" aria-hidden="true">
                  {financialHealth.tone === "good"
                    ? "✓"
                    : financialHealth.tone === "bad"
                      ? "!"
                      : "i"}
                </div>

                <div>
                  <small>Saúde financeira</small>
                  <strong>{financialHealth.label}</strong>
                  <span>
                    Margem {percentBR(financialHealth.margin)}% •{" "}
                    {financialHealth.description}
                  </span>
                </div>
              </aside>
            </section>

            <section className="frep-summary">
              <SummaryCard
                tone="up"
                icon="+"
                label="Receita"
                value={moneyBR(receita)}
                description="Entradas registradas"
              />

              <SummaryCard
                tone="down"
                icon="−"
                label="Despesas"
                value={moneyBR(despesa)}
                description="Saídas registradas"
              />

              <SummaryCard
                tone={`wallet-${getBalanceTone(lucro)}`}
                icon="≋"
                label="Lucro"
                value={moneyBR(lucro)}
                description="Receita menos despesa"
              />
            </section>

            <section className="frep-panel">
              <div className="frep-panel__head">
                <div>
                  <span>Faturamento</span>
                  <h2>Faturamento mensal</h2>
                  <p>
                    Visualize a evolução do faturamento ao longo dos meses
                    disponíveis no relatório.
                  </p>
                </div>

                {lastUpdatedAt ? (
                  <div className="frep-updated">
                    Atualizado às {getTimeBR(lastUpdatedAt)}
                  </div>
                ) : null}
              </div>

              <div className="frep-chart-card">
                <BarChartSvg data={faturamentoMensal} />
              </div>
            </section>

            <section className="frep-panel">
              <div className="frep-panel__head frep-panel__head--with-tools">
                <div>
                  <span>Movimentações</span>
                  <h2>Últimas transações</h2>
                  <p>
                    Consulte as entradas e saídas mais recentes do período.
                  </p>
                </div>

                <div className="frep-tx-totals">
                  <strong>{transactionTotals.total}</strong>
                  <small>transações</small>
                </div>
              </div>

              <div className="frep-toolbar">
                <label className="frep-search">
                  <span aria-hidden="true">⌕</span>
                  <input
                    value={txQuery}
                    onChange={(event) => setTxQuery(event.target.value)}
                    placeholder="Buscar transação..."
                  />

                  {txQuery ? (
                    <button
                      type="button"
                      onClick={() => setTxQuery("")}
                      aria-label="Limpar busca"
                    >
                      ×
                    </button>
                  ) : null}
                </label>

                <div className="frep-filter-group" aria-label="Filtro de transações">
                  {TRANSACTION_FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      className={txFilter === filter.value ? "is-active" : ""}
                      onClick={() => setTxFilter(filter.value)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="frep-quick-totals">
                <span>
                  Entradas <b>{moneyBR(transactionTotals.entrada)}</b>
                </span>
                <span>
                  Saídas <b>{moneyBR(transactionTotals.saida)}</b>
                </span>
              </div>

              {filteredTransactions.length === 0 ? (
                <div className="frep-empty-mini">
                  Nenhuma transação encontrada com os filtros atuais.
                </div>
              ) : (
                <div className="frep-tx">
                  {filteredTransactions.map((transaction) => {
                    const isIncome = transaction.tipo === "receita";

                    return (
                      <article className="frep-tx__item" key={transaction.id}>
                        <div
                          className={`frep-tx__icon ${isIncome ? "is-in" : "is-out"}`}
                          aria-hidden="true"
                        >
                          {isIncome ? "+" : "−"}
                        </div>

                        <div className="frep-tx__main">
                          <div className="frep-tx__title">
                            {transaction.descricao || "Transação"}
                          </div>
                          <div className="frep-tx__sub">
                            {formatDateBR(transaction.data)}
                            {transaction.categoria ? ` • ${transaction.categoria}` : ""}
                          </div>
                        </div>

                        <div className={`frep-tx__value ${isIncome ? "is-in" : "is-out"}`}>
                          {isIncome ? "+" : "−"}
                          {moneyBR(transaction.valor)}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="frep-bottom-actions">
              <button className="frep-btn frep-btn--ghost-dark" type="button" onClick={goBack}>
                Voltar aos relatórios
              </button>

              <button className="frep-btn frep-btn--primary" type="button" onClick={goFinance}>
                Abrir financeiro
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
